/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../buttons/Button";
import {
  errorAlert,
  makeApiRequest,
  successAlert,
  fetchFixedStationVoiceIPV4Address,
  fetchHostControlIPV4Address,
  fetchHostVoiceIPV4Address,
  fetchHostControlPort,
  fetchDataIPV4AddressValue,
  fetchDataPortValue,
  fetchConnectivityTxTimerValue,
  fetchFSHeartbeatPeriodValue,
  fetchHostHeartbeatPeriodValue,
  fetchFixedStationVoterReportingPeriodValue,
  fetchFixedStationTransmitterReportIntervalValue,
  getUniqueCombination,
} from "../../../util";
import AuthContext from "../../../store/authContext";
import {
  ADD_DFSI_CONNECTION_FORM_FIELDS,
  fieldNameMapping,
} from "../../../constants";
import Form from "../../../ui/Form/Form";
import NumberField from "../../inputs/NumberField";
import TextField from "../../inputs/TextField";
import DropdownField from "../../inputs/DropdownField";
import { IDFSIConnectionData } from "../../../types";
import DeleteModal, { DeleteModalIcons } from "../../modal/DeleteModal";
import {
  CREATE_DFSI_CONNECTION_TRANSLATIONS,
  FORM_VALIDATION_TRANSLATIONS,
} from "../../../translations/util";
import ToggleField from "../../inputs/ToggleField";
import OrgContext from "../../../store/orgContext";

interface IProps {
  org: string;
}
const set = new Set();

const isFormInvalid = ({ data }: { data: any }) => {
  if (!data) return true;

  const isConnectionFormInvalid = ADD_DFSI_CONNECTION_FORM_FIELDS.map(
    (rows: any) => {
      const res = rows.fields.map((field: any) => {
        switch (field.type) {
          case "TEXT":
            return field.isInputInvalid?.({
              version: data["DFSI-VERSION"],
              val: data[field.name],
              role: data["IWF-ROLE"],
            });
          case "DROPDOWN":
          case "NUMBER":
            return field.isInputInvalid?.({
              version: data["DFSI-VERSION"],
              val: data[field.name],
              role: data["IWF-ROLE"],
            });
          default:
            return false;
        }
      });
      return res.reduce((acc: any, el: any) => acc || el);
    }
  ).reduce((acc: any, el: any) => acc || el);
  return isConnectionFormInvalid;
};

const NewDFSIConnection = ({ org }: IProps) => {
  const { token } = useContext(AuthContext);
  const { selectedOrg } = useContext(OrgContext);
  const [loading, setLoading] = useState(false);
  const [allDFSIconnections, setAllDFSIconnections] = useState<any[]>([]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    set.clear();
    return () => {
      set.clear();
    };
  }, []);

  const [connectionData, setConnectionData] = useState<IDFSIConnectionData>({
    "CONNECTION-ID": undefined,
    "DFSI-VERSION": undefined,
    "IWF-ROLE": undefined,
    "HOST-CONTROL-IPV4-ADDR": undefined,
    "HOST-CONTROL-PORT": undefined,
    "HOST-VOICE-IPV4-ADDR": undefined,
    "VOICE-PORT": undefined,
    "DATA-IPV4-ADDR": undefined, // v2 only
    "DATA-PORT": undefined, // v2 only
    "FIXED-STATION-CONTROL-IPV4-ADDR": undefined,
    "FIXED-STATION-VOICE-IPV4-ADDR": undefined, // v1 only
    "FIXED-STATION-CONTROL-PORT": undefined,
    "CONNECTIVITY-TX-TIMER": undefined,
    "CONNECTIVITY-LOSS-LIMIT": undefined,
    "FS-HEARTBEAT-PERIOD": undefined, // v1 only
    "HOST-HEARTBEAT-PERIOD": undefined, // v1 only
    "CONTROL-RETRY-TIME": undefined,
    "CONTROL-ATTEMPT-LIMIT": undefined,
    "OUTBOUND-MAX-CAI-DATA-BLOCKS": undefined, // v2 only
    "INBOUND-MAX-CAI-DATA-BLOCKS": undefined, // v2 only
    "ACKNOWLEDGEMENT-DELAY": undefined,
    "FIXED-STATION-TRANSMITTER-REPORT-INTERVAL": undefined,
    "INBOUND-REPEAT": true,
    "FIXED-STATION-VOTER-REPORTING-PERIOD": undefined,
  });

  const validateUniqueCombos = ({
    allRecords,
    currentRecord,
    fieldCombos,
  }: {
    allRecords: Array<Record<string, any>>;
    currentRecord: Record<string, any>;
    fieldCombos?: string[][];
  }): boolean => {
    const failedCombo = fieldCombos?.find((combo) => {
      return allRecords.some((record) => {
        return combo.every((field) => {
          return record[field] === currentRecord[field];
        });
      });
    });

    if (failedCombo) {
      errorAlert(
        `The combination of ${fieldNameMapping[failedCombo[0]]} and ${
          fieldNameMapping[failedCombo[1]]
        } is not unique`
      );
      return false;
    }
    return true;
  };

  const body = {
    "CONNECTION-ID": connectionData["CONNECTION-ID"],
    "IWF-ROLE": connectionData["IWF-ROLE"],
    "DFSI-VERSION":
      connectionData["DFSI-VERSION"] && +connectionData["DFSI-VERSION"],
    "HOST-CONTROL-IPV4-ADDR":
      connectionData["HOST-CONTROL-IPV4-ADDR"] &&
      fetchHostControlIPV4Address(connectionData),
    "HOST-CONTROL-PORT":
      connectionData["HOST-CONTROL-PORT"] &&
      fetchHostControlPort(connectionData),
    "HOST-VOICE-IPV4-ADDR":
      connectionData["HOST-VOICE-IPV4-ADDR"] &&
      fetchHostVoiceIPV4Address(connectionData),
    "VOICE-PORT": connectionData["VOICE-PORT"],
    "DATA-IPV4-ADDR":
      connectionData["DATA-IPV4-ADDR"] &&
      fetchDataIPV4AddressValue(connectionData),
    "DATA-PORT":
      connectionData["DATA-PORT"] && fetchDataPortValue(connectionData),
    "FIXED-STATION-CONTROL-IPV4-ADDR":
      connectionData["FIXED-STATION-CONTROL-IPV4-ADDR"],
    "FIXED-STATION-VOICE-IPV4-ADDR":
      connectionData["FIXED-STATION-VOICE-IPV4-ADDR"] &&
      fetchFixedStationVoiceIPV4Address(connectionData),
    "FIXED-STATION-CONTROL-PORT": connectionData["FIXED-STATION-CONTROL-PORT"],
    "CONNECTIVITY-TX-TIMER":
      connectionData["CONNECTIVITY-TX-TIMER"] &&
      fetchConnectivityTxTimerValue(connectionData),
    "CONNECTIVITY-LOSS-LIMIT": connectionData["CONNECTIVITY-LOSS-LIMIT"],
    "FS-HEARTBEAT-PERIOD":
      connectionData["FS-HEARTBEAT-PERIOD"] &&
      fetchFSHeartbeatPeriodValue(connectionData),
    "HOST-HEARTBEAT-PERIOD":
      connectionData["HOST-HEARTBEAT-PERIOD"] &&
      fetchHostHeartbeatPeriodValue(connectionData),
    "CONTROL-RETRY-TIME": connectionData["CONTROL-RETRY-TIME"],
    "CONTROL-ATTEMPT-LIMIT": connectionData["CONTROL-ATTEMPT-LIMIT"],
    ...(connectionData["DFSI-VERSION"] &&
      +connectionData["DFSI-VERSION"] === 2 && {
        "OUTBOUND-MAX-CAI-DATA-BLOCKS":
          connectionData["OUTBOUND-MAX-CAI-DATA-BLOCKS"],
      }),
    ...(connectionData["DFSI-VERSION"] &&
      +connectionData["DFSI-VERSION"] === 2 && {
        "INBOUND-MAX-CAI-DATA-BLOCKS":
          connectionData["INBOUND-MAX-CAI-DATA-BLOCKS"],
      }),
    "ACKNOWLEDGEMENT-DELAY": connectionData["ACKNOWLEDGEMENT-DELAY"],
    "INBOUND-REPEAT": connectionData["INBOUND-REPEAT"],
    "FIXED-STATION-VOTER-REPORTING-PERIOD":
      connectionData["FIXED-STATION-VOTER-REPORTING-PERIOD"] &&
      fetchFixedStationVoterReportingPeriodValue(connectionData),
    "FIXED-STATION-TRANSMITTER-REPORT-INTERVAL":
      connectionData["FIXED-STATION-TRANSMITTER-REPORT-INTERVAL"] &&
      fetchFixedStationTransmitterReportIntervalValue(connectionData),
  };
  const createDFSIConnection = async () => {
    setLoading(true);

    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/app-config/dfsi-connections`,
        method: "POST",
        body: JSON.stringify(body),
        token,
        org,
      });
      successAlert(CREATE_DFSI_CONNECTION_TRANSLATIONS.successAlertMessage);
      navigate("/configurationManagement/dfsiConnections");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllDFSIConnections = async () => {
    setLoading(true);
    try {
      if (!selectedOrg) return;

      let page = 1;
      const pageSize = 100;
      let allRows: any[] = [];
      let totalPages = 1;

      do {
        const queryParams = new URLSearchParams({
          page: `${page}`,
          pageSize: `${pageSize}`,
        });

        // eslint-disable-next-line no-await-in-loop
        const response = await makeApiRequest<{
          Rows: Array<any>;
          NbrOfRows: number;
          NbrOfPages: number;
          Page: number;
        }>({
          path: `/api/v1/app-config/dfsi-connections?${queryParams}`,
          token,
          org: selectedOrg.id,
        });
        allRows = [...allRows, ...response.Rows];
        totalPages = response.NbrOfPages;
        // eslint-disable-next-line no-plusplus
        page++;
      } while (page <= totalPages);

      setAllDFSIconnections(allRows); // Final set after all data is fetched
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllDFSIConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="data-form">
        <Form>
          <div className="data-tabcontent data-card">
            {ADD_DFSI_CONNECTION_FORM_FIELDS.map(
              (rows: {
                cols: number;
                fields: {
                  name: keyof IDFSIConnectionData;
                  tooltipText?: string;
                  label: string;
                  disabled?: (dfsiVersion?: number, role?: string) => boolean;
                  type: string;
                  invalidText?: string | null;
                  isInputInvalid?: ({
                    version,
                    val,
                  }: {
                    version?: number;
                    val?: string | number;
                  }) => boolean;
                  min?: number;
                  max?: number;
                  items?: [];
                }[];
              }) => {
                return (
                  <div
                    className={`grid grid-cols-${rows.cols} gap-4 form-control single-item`}
                  >
                    {rows.fields.map((field) => {
                      switch (field.type) {
                        case "TOGGLE":
                          return (
                            <ToggleField
                              labelText={field.label}
                              onChange={(val) =>
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: val,
                                })
                              }
                              value={connectionData[field.name] as boolean}
                              tooltipText={field.tooltipText}
                              defaultToggled
                            />
                          );
                        case "NUMBER":
                          return (
                            <NumberField
                              onChange={(val) => {
                                set.add(field.name);
                                if (!val || !Number.isNaN(+val))
                                  setConnectionData({
                                    ...connectionData,
                                    [field.name]: val ? +val : undefined,
                                  });
                              }}
                              disabled={field.disabled?.(
                                connectionData["DFSI-VERSION"],
                                connectionData["IWF-ROLE"]
                              )}
                              label={field.label}
                              placeholder={field.label}
                              max={field.max || 0}
                              min={field.min || 0}
                              invalid={
                                set.has(field.name) &&
                                field.isInputInvalid?.({
                                  version: connectionData["DFSI-VERSION"],
                                  val: connectionData[field.name] as number,
                                })
                              }
                              value={connectionData[field.name] as number}
                              invalidText={`"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`}
                              tooltipText={field.tooltipText}
                              required
                            />
                          );
                        case "DROPDOWN":
                          return (
                            <DropdownField
                              className="dropdown-field"
                              label={field.label}
                              selectedItem={connectionData[field.name]}
                              items={field.items}
                              onChange={({
                                selectedItem,
                              }: {
                                selectedItem: string;
                              }) =>
                                field.name === "DFSI-VERSION"
                                  ? setConnectionData({
                                      ...connectionData,
                                      [field.name]: +selectedItem,
                                    })
                                  : setConnectionData({
                                      ...connectionData,
                                      [field.name]: selectedItem,
                                    })
                              }
                              tooltipText={field.tooltipText}
                              required
                            />
                          );
                        default:
                          return (
                            <TextField
                              onChange={(val) => {
                                set.add(field.name);
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: val,
                                });
                              }}
                              disabled={field.disabled?.(
                                connectionData["DFSI-VERSION"],
                                connectionData["IWF-ROLE"]
                              )}
                              placeholder={field.label}
                              value={connectionData[field.name] as string}
                              invalid={
                                set.has(field.name) &&
                                field.isInputInvalid?.({
                                  version: connectionData["DFSI-VERSION"],
                                  val: connectionData[field.name] as string,
                                })
                              }
                              invalidText={
                                field.invalidText ||
                                FORM_VALIDATION_TRANSLATIONS.required(
                                  field.label
                                )
                              }
                              labelText={field.label}
                              tooltipText={field.tooltipText}
                              required
                            />
                          );
                      }
                    })}
                  </div>
                );
              }
            )}
          </div>
        </Form>
      </div>
      <DeleteModal
        modalHeaderText={CREATE_DFSI_CONNECTION_TRANSLATIONS.deleteModal.header}
        modalDescriptionText={
          CREATE_DFSI_CONNECTION_TRANSLATIONS.deleteModal.description
        }
        onDelete={() => navigate("/configurationManagement/dfsiConnections")}
        open={open}
        setOpen={setOpen}
        confirmationButtonLabel={
          CREATE_DFSI_CONNECTION_TRANSLATIONS.deleteModal.goBackButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <div className="flex justify-end form-footer">
        <div className="mr-2">
          <Button
            className="w-full"
            type="button"
            kind="tertiary"
            disabled={false}
            label={CREATE_DFSI_CONNECTION_TRANSLATIONS.cancelButtonLabel}
            onClick={() => setOpen(true)}
            dataCy="cancelCreateDFSIconnection-"
          />
        </div>

        <div>
          <Button
            className="w-full"
            type="button"
            loading={loading}
            disabled={isFormInvalid({ data: connectionData })}
            onClick={() => {
              const result = validateUniqueCombos({
                allRecords: allDFSIconnections,
                currentRecord: connectionData,
                fieldCombos:
                  connectionData["DFSI-VERSION"] && connectionData["IWF-ROLE"]
                    ? getUniqueCombination(
                        connectionData["DFSI-VERSION"],
                        connectionData["IWF-ROLE"]
                      )
                    : [[]],
              });
              if (result) {
                createDFSIConnection();
              }
            }}
            label={CREATE_DFSI_CONNECTION_TRANSLATIONS.createButtonLabel}
            dataCy="createDFSIconnection-"
          />
        </div>
      </div>
    </>
  );
};

export default NewDFSIConnection;
